import { toaster } from "@/components/ui/toaster";
import { EstadosSolicitudVoleo } from "@/constants/enums/status/entity-status-ids";
import { AppRoutes } from "@/constants/routes";
import { uploadFileToStorage } from "@/services/file.service";
import {
  getVoleoArchivoUploadUrl,
  getVoleoRequestDetail,
  putVoleoArchivoUploadUrl,
  SolicitudVoleoDetail,
  updateVoleoRequest,
} from "@/services/voleo-requests.service";
import { getAxiosErrorMessage } from "@/utils/axios-error-message";
import { handlePermissionError } from "@/utils/permission-check";
import { AxiosError } from "axios";
import { FieldValues } from "react-hook-form";
import { NavigateFunction } from "react-router-dom";

function getFileContentType(file: File): string {
  const ext = file.name.split(".").pop()?.toLowerCase();
  switch (ext) {
    case "zip":
      return "application/zip";
    default:
      return file.type || "application/octet-stream";
  }
}

export const fetchVoleoRequestDetail = async (params: {
  id?: string;
  showLoad?: boolean;
  setLoadingDetail: (value: boolean) => void;
  setDetail: (value: SolicitudVoleoDetail | null) => void;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}) => {
  const { id, showLoad = true, setLoadingDetail, setDetail, navigate, fetchPermissions } =
    params;

  try {
    if (!id) return;
    if (showLoad) setLoadingDetail(true);
    const response = await getVoleoRequestDetail(Number(id));
    setDetail(response);
  } catch (error) {
    const errorObj = error as AxiosError;
    const handled = await handlePermissionError(error, navigate, fetchPermissions);
    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(errorObj, "No se pudo cargar la solicitud."),
      });
    }
    throw error;
  } finally {
    if (showLoad) setLoadingDetail(false);
  }
};

export const rejectVoleoRequest = async (params: {
  detailId?: number;
  idParam?: string;
  event?: FieldValues;
  setLoadingReject: (value: boolean) => void;
  setOpenRejectModal: (value: boolean) => void;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  fetchDetail: (showLoad?: boolean) => Promise<void>;
}) => {
  const {
    detailId,
    idParam,
    event,
    setLoadingReject,
    setOpenRejectModal,
    navigate,
    fetchPermissions,
    fetchDetail,
  } = params;

  if (!detailId) return;
  setLoadingReject(true);

  try {
    await updateVoleoRequest({
      id: detailId,
      idEstado: EstadosSolicitudVoleo.Rechazada,
      motivoRechazo: event?.request_detail,
    });

    toaster.create({
      type: "success",
      title: "Solicitud de voleo rechazada exitosamente.",
    });

    setOpenRejectModal(false);
    await fetchDetail(false);
  } catch (error) {
    const errorObj = error as AxiosError;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.VOLEO_REQUEST_DETAIL.replace(":id", String(idParam)),
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(errorObj, "No se pudo rechazar la solicitud."),
      });
    }
    throw error;
  } finally {
    setLoadingReject(false);
  }
};

export const markDoneVoleoRequest = async (params: {
  detailId?: number;
  idParam?: string;
  values: { quantity: number; fechaRealizacion: string };
  setLoadingDone: (value: boolean) => void;
  setOpenDoneModal: (value: boolean) => void;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  fetchDetail: (showLoad?: boolean) => Promise<void>;
}) => {
  const {
    detailId,
    idParam,
    values,
    setLoadingDone,
    setOpenDoneModal,
    navigate,
    fetchPermissions,
    fetchDetail,
  } = params;

  if (!detailId) return;
  setLoadingDone(true);

  try {
    await updateVoleoRequest({
      id: detailId,
      idEstado: EstadosSolicitudVoleo.Realizado,
      hectareasEjecutadas: Number(values.quantity),
      fechaRealRealizacion: values.fechaRealizacion,
    });

    toaster.create({
      type: "success",
      title: "Solicitud de voleo realizada exitosamente.",
    });

    setOpenDoneModal(false);
    await fetchDetail(false);
  } catch (error) {
    const errorObj = error as AxiosError;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.VOLEO_REQUEST_DETAIL.replace(":id", String(idParam)),
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(
          errorObj,
          "No se pudo marcar como realizada la solicitud.",
        ),
      });
    }
    throw error;
  } finally {
    setLoadingDone(false);
  }
};

export const closeVoleoRequest = async (params: {
  detailId?: number;
  idParam?: string;
  setLoadingClose: (value: boolean) => void;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  fetchDetail: (showLoad?: boolean) => Promise<void>;
}) => {
  const { detailId, idParam, setLoadingClose, navigate, fetchPermissions, fetchDetail } =
    params;

  if (!detailId) return;
  setLoadingClose(true);

  try {
    await updateVoleoRequest({
      id: detailId,
      idEstado: EstadosSolicitudVoleo.Cerrado,
    });

    toaster.create({
      type: "success",
      title: "Solicitud de voleo cerrada exitosamente.",
    });

    await fetchDetail(false);
  } catch (error) {
    const errorObj = error as AxiosError;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.VOLEO_REQUEST_DETAIL.replace(":id", String(idParam)),
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(errorObj, "No se pudo cerrar la solicitud."),
      });
    }
    throw error;
  } finally {
    setLoadingClose(false);
  }
};

export const attachVoleoRequestFile = async (params: {
  detailId?: number;
  idParam?: string;
  file: File;
  setLoadingFile: (value: boolean) => void;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  fetchDetail: (showLoad?: boolean) => Promise<void>;
}) => {
  const { detailId, idParam, file, setLoadingFile, navigate, fetchPermissions, fetchDetail } =
    params;
  if (!detailId) return;

  setLoadingFile(true);
  try {
    const contentType = getFileContentType(file);
    const uploadData = await getVoleoArchivoUploadUrl(detailId, {
      tipoArchivo: contentType,
      nombreDocumento: file.name,
    });
    if (!uploadData) throw new Error();

    await uploadFileToStorage({
      uploadUrl: uploadData.uploadUrl,
      buffer: file,
      contentType,
    });

    toaster.create({
      type: "success",
      title: "Archivo adjuntado. Puede tardar unos minutos en estar disponible.",
    });

    await fetchDetail(false);
  } catch (error) {
    const errorObj = error as AxiosError;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.VOLEO_REQUEST_DETAIL.replace(":id", String(idParam)),
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(errorObj, "No se pudo adjuntar el archivo."),
      });
    }
    throw error;
  } finally {
    setLoadingFile(false);
  }
};

export const replaceVoleoRequestFileUpload = async (params: {
  detailId?: number;
  idParam?: string;
  idArchivoAdjunto?: number | null;
  file: File;
  setLoadingFile: (value: boolean) => void;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  fetchDetail: (showLoad?: boolean) => Promise<void>;
}) => {
  const {
    detailId,
    idParam,
    idArchivoAdjunto,
    file,
    setLoadingFile,
    navigate,
    fetchPermissions,
    fetchDetail,
  } = params;
  if (!detailId || !idArchivoAdjunto) return;

  setLoadingFile(true);
  try {
    const contentType = getFileContentType(file);
    const uploadData = await putVoleoArchivoUploadUrl(detailId, {
      idArchivoAdjunto,
      tipoArchivo: contentType,
      nombreDocumento: file.name,
    });
    if (!uploadData) throw new Error();

    await uploadFileToStorage({
      uploadUrl: uploadData.uploadUrl,
      buffer: file,
      contentType,
    });

    toaster.create({
      type: "success",
      title: "Archivo reemplazado. Puede tardar unos minutos en estar disponible.",
    });

    await fetchDetail(false);
  } catch (error) {
    const errorObj = error as AxiosError;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.VOLEO_REQUEST_DETAIL.replace(":id", String(idParam)),
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(errorObj, "No se pudo reemplazar el archivo."),
      });
    }
    throw error;
  } finally {
    setLoadingFile(false);
  }
};
